import { Controller } from "@hotwired/stimulus";
import confetti from "canvas-confetti";

const FIREWORK_INTERVAL_MILLISECONDS = 400;

/** The celebration after a result is confirmed. */
export default class extends Controller<HTMLElement> {
  static override values = {
    autoplay: Boolean,
    autoplayDuration: Number,
    burst: Boolean,
    sound: String,
  };

  declare readonly autoplayDurationValue: number;
  declare readonly autoplayValue: boolean;
  declare readonly burstValue: boolean;
  declare readonly soundValue: string;

  private interval: ReturnType<typeof setInterval> | undefined;

  override connect(): void {
    if (!this.autoplayValue) {
      return;
    }

    /* One burst that falls, rather than a display that keeps going. `ConfettiView` throws a
           single handful and lets it land - about five seconds of falling, not five seconds of
           firing - and a match page that kept launching would read as a party rather than as
           "you won this". The birthday on the profile is the other case and keeps the display. */
    if (this.burstValue) {
      this.fire();

      return;
    }

    this.playFireworks(this.autoplayDurationValue);
  }

  override disconnect(): void {
    /* The firework loop outlives the element otherwise, firing into a page that has navigated
           away from the match it was celebrating. */
    if (this.interval !== undefined) {
      clearInterval(this.interval);
      this.interval = undefined;
    }
  }

  fire(): void {
    if (this.soundValue !== "") {
      void new Audio(this.soundValue).play();
    }

    const count = 200;
    const defaults = { origin: { y: 0.7 } };
    const burst = (particleRatio: number, options: confetti.Options): void => {
      void confetti({
        ...defaults,
        ...options,
        particleCount: Math.floor(count * particleRatio),
      });
    };

    burst(0.25, { spread: 26, startVelocity: 55 });
    burst(0.2, { spread: 60 });
    burst(0.35, { decay: 0.91, scalar: 0.8, spread: 100 });
    burst(0.1, { decay: 0.92, scalar: 1.2, spread: 120, startVelocity: 25 });
    burst(0.1, { spread: 120, startVelocity: 45 });
  }

  playFireworks(durationSeconds: number): void {
    const durationMilliseconds = durationSeconds * 1000;
    const animationEnd = Date.now() + durationMilliseconds;
    const defaults = { spread: 360, startVelocity: 40, ticks: 60, zIndex: 0 };

    this.interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) {
        this.disconnect();

        return;
      }

      const particleCount = 50 * (timeLeft / durationMilliseconds);

      /* Since particles fall down, start a bit higher than random. */
      void confetti({
        ...defaults,
        origin: { x: this.randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
        particleCount,
      });
      void confetti({
        ...defaults,
        origin: { x: this.randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        particleCount,
      });
    }, FIREWORK_INTERVAL_MILLISECONDS);
  }

  randomInRange(min: number, max: number): number {
    return Math.random() * (max - min) + min;
  }
}
